import { FaqComponent, Icon } from '@arcadiapower/shrike';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import secureLockDark from 'static/secure-lock-dark.svg';
import secureLockLight from 'static/secure-lock-light.svg';
import { Button } from '~/components/button';
import { FaqButton } from '~/components/button/button.style';
import { Wrapper } from '~/components/customer-details/customer-details.style';
import { DialogSelector, UTILITY_SYNC } from '~/components/dialogs';
import { Disclaimer } from '~/components/disclaimer';
import { type ImageType, ImageSelector, UTILITY_CONNECT_IMAGE } from '~/components/image';
import { StyledIcon } from '~/components/image/image.style';
import { Container, TwoColumnGrid } from '~/components/layout';
import { Link } from '~/components/link';
import { Separator } from '~/components/separator';
import { Description, Title, Text } from '~/components/typography';
import { UtilityConnectButton } from '~/components/utility-connect-button';
import { useCopy } from '~/hooks/use-copy';
import { LDClient, LDFlags } from '~/providers';
import { Utility, User, EnrollmentDataSource } from '~/typings/context';
import { UtilityConnectStatus } from '~/typings/utility-connect';
import { IS_TEST, INTERNAL_ROUTES, SPONSORED_SOLAR_ROUTES } from '~/utils/constants';
import { getSubRoute } from '~/utils/redirects';
import { TRACK } from '~/utils/tracking';
import { faqData } from './faq.data';

export const Credentials = ({
  context,
  error,
  goTo,
  next,
  overrideCopy,
  skip,
  flags,
  onError,
  updateStore,
}: CredentialsProps) => {
  const copy = useCopy(context, defaultCopy, overrideCopy);
  const hasInvalidCredentials =
    context.verificationStatus! === 'incorrect' || context.verificationStatus! === 'unverified';
  const goToAccountNumber = () => {
    if (goTo?.accountNumber) {
      // even thought this is fallback screen, for some reason
      // we are sending utility_connect to the signup API.
      updateStore?.('utility_connect');
      goTo.accountNumber();
    }
  };
  const faqRef = useRef<HTMLDivElement>(null);
  const backToTopRef = useRef<HTMLDivElement>(null);
  const [skipUtilityCredential, setSkipUtilityCredential] = useState(false);

  useEffect(() => {
    LDClient.on('change', () => {
      setSkipUtilityCredential(LDClient.variation(LDFlags.skipUtilityConnect));
    });
  }, []);

  const handleNext = ({ status }: { status: UtilityConnectStatus }) => {
    if (status === 'no_submit') return;
    const unverifiedStatuses: UtilityConnectStatus[] = ['rejected', 'error', 'timed_out'];
    if (context.funnelId === 'sponsored-solar' && unverifiedStatuses.includes(status)) {
      setSkipUtilityCredential(true);
      onError?.();
      return;
    }
    updateStore?.('utility_connect');
    next({ utilityConnectStatus: status });
  };

  const skipUtilityConnect = () => {
    updateStore?.('none');
    navigate(
      getSubRoute(INTERNAL_ROUTES.SPONSORED_SOLAR, SPONSORED_SOLAR_ROUTES.PROOF_DOCS_UPLOAD),
    );
  };

  const handleSkip = () => {
    if (skip) {
      skip();
    } else {
      skipUtilityConnect();
    }
  };
  const navigate = useNavigate();

  return (
    <Wrapper ref={backToTopRef} style={{ border: 'none' }}>
      <TwoColumnGrid testId="screen-Credentials">
        <TwoColumnGrid.Primary>
          {!!flags?.showStep && (
            <Text color="secondary" fontWeight="700" fontSize="16px">
              {copy.step}
            </Text>
          )}
          <Title>{copy.title}</Title>
          {hasInvalidCredentials ? (
            <Container>
              <Description>{copy.invalidDescriptionOne}</Description>
              <Description>{copy.invalidDescriptionTwo}</Description>
              {(goTo?.accountNumber || goTo?.billUpload) && (
                <Description>{copy.invalidDescriptionThree}</Description>
              )}
            </Container>
          ) : (
            <>
              <Description mb={5}>{copy.description}</Description>
              {copy.descriptionTwo && <Description mb={5}>{copy.descriptionTwo}</Description>}
            </>
          )}
          {flags?.showFaqButton ? (
            <FaqButton
              fullWidth={false}
              mode="default"
              onClick={() => {
                faqRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {' '}
              <Icon
                color="primary"
                icon="InfoCircleInverted"
                margin="5px"
                opacity="high"
                path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                scale={2}
                title="InfoCircleInverted"
              />
              View FAQs
            </FaqButton>
          ) : null}
          {copy.learnMoreModal && copy.learnMoreText && (
            <Description mb={6}>
              {copy.learnMoreModal && (
                <Icon icon="QuestionCircleInverted" margin={{ bottom: '2px', right: '8px' }} />
              )}
              <DialogSelector
                type={copy.learnMoreModal}
                target={
                  <Link
                    color="primary"
                    aria-label="Utility dialog"
                    track={TRACK.learnMoreCsClicked}
                  >
                    {copy.learnMoreText}
                  </Link>
                }
              />
            </Description>
          )}
          <UtilityConnectButton
            error={error}
            onClose={handleNext}
            utilityConnectToken={context?.utilityConnectToken}
            utilityId={context?.utility?.id}
            onError={onError}
          >
            <StyledIcon src={secureLockLight} data-testid="credential-secure-icon" />
            {error && flags?.allowUtilityReconnection
              ? copy.retryUtilityConnectButton
              : copy.syncUtilityButton}
          </UtilityConnectButton>
          {goTo?.accountNumber && (
            <>
              <Separator innerText="or" />
              <Button
                testId="choose-account-number-button"
                mode="default"
                onClick={() => goToAccountNumber()}
              >
                <StyledIcon src={secureLockDark} data-testid="account-info-secure-icon" />
                {copy.accountNumberButton}
              </Button>
            </>
          )}
          {goTo?.billUpload && (
            <>
              <Separator innerText="or" />
              <Button
                testId="choose-bill-upload-button"
                mode="default"
                onClick={() => goTo?.billUpload?.()}
              >
                <StyledIcon src={secureLockDark} data-testid="bill-upload-secure-icon" />
                {copy.billUploadButton}
              </Button>
            </>
          )}
          {(skip ||
            skipUtilityCredential ||
            (error && flags?.allowSkipOnUtilityConnectFailure)) && (
            <>
              <Separator innerText="or" />
              <Button testId="skip-creds-button" mode="default" onClick={handleSkip}>
                {copy.skipButton}
              </Button>
            </>
          )}
          {copy.disclaimer && <Disclaimer mt={7}>{copy.disclaimer}</Disclaimer>}
        </TwoColumnGrid.Primary>
        <TwoColumnGrid.Secondary>
          {flags?.showFaqButton ? (
            <Wrapper
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent data={faqData(context)} header="Frequently Asked Questions" />
            </Wrapper>
          ) : (
            <ImageSelector type={copy.imageType} />
          )}
          {IS_TEST &&
            (['verified', 'timed_out', 'rejected', 'error'] as UtilityConnectStatus[]).map(
              utilityConnectStatus => (
                <button
                  data-testid={`test-submit-${utilityConnectStatus}`}
                  key={utilityConnectStatus}
                  onClick={() => next({ utilityConnectStatus })}
                >
                  Test submit
                </button>
              ),
            )}
        </TwoColumnGrid.Secondary>
        {flags?.showFaqButton ? (
          <TwoColumnGrid.MobileOnlySecondary>
            <Wrapper
              ref={faqRef}
              gridColumn="auto / span 2"
              display="flex"
              flexGrow={1}
              style={{ borderRadius: '0px' }}
            >
              <FaqComponent data={faqData(context)} header="Frequently Asked Questions" />
            </Wrapper>
            <Wrapper
              onClick={() => {
                backToTopRef?.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                textDecoration: 'underline',
                textUnderlineOffset: ' 2px',
              }}
            >
              <Text textAlign="center" mt={5} paddingBottom={5} textStyle="heading600">
                Back to top
                <Icon
                  color="primary"
                  icon="ArrowUp"
                  margin="5px"
                  opacity="high"
                  path="M 1,3 A 2,2 0,0,1 5,3 A 2,2 0,0,1 9,3 Q 9,6 5,9 Q 1,6 1,3 z"
                  scale={2}
                  title="ArrowUp"
                />
              </Text>
            </Wrapper>
          </TwoColumnGrid.MobileOnlySecondary>
        ) : null}
      </TwoColumnGrid>
    </Wrapper>
  );
};

export const defaultCopy = (context: CredentialsContext) => {
  const utility = context?.utility?.displayName;
  const hasInvalidCredentials =
    context.verificationStatus! === 'incorrect' || context.verificationStatus! === 'unverified';
  return {
    title: `${
      hasInvalidCredentials ? 'Securely re-' : 'Securely '
    }integrate your utility account with Arcadia.`,
    description:
      'Integrating with your utility helps us understand the amount of energy you use so that we can maximize your monthly savings. Establishing this connection does not change anything about your utility account or how you get energy.',
    descriptionTwo:
      'Please choose how you want to integrate your utility account with your Arcadia account.',
    disclaimer:
      'Arcadia is an independent service provider committed to putting you and the planet first. Arcadia is not affiliated with any utility or governmental agency.',
    imageType: UTILITY_CONNECT_IMAGE as ImageType,
    invalidDescriptionOne: `There was an error with the information you provided for your ${utility} account.`,
    invalidDescriptionTwo:
      'Integrating with your utility helps us understand the amount of energy you use so that we can maximize your monthly savings. Establishing this connection does not change anything about your utility account or how you get energy',
    invalidDescriptionThree:
      'Please choose how you want to re-integrate your utility account with your Arcadia account.',
    learnMoreModal: UTILITY_SYNC,
    learnMoreText: 'Why does Arcadia need to integrate with your utility?',
    syncUtilityButton: 'Enter utility login credentials',
    accountNumberButton: 'Enter utility account information',
    billUploadButton: 'Upload a picture of your bill',
    skipButton: 'Skip for now',
    retryUtilityConnectButton: 'Try again',
    step: 'Step 2 of 4',
  };
};

export type CredentialsCopy = ReturnType<typeof defaultCopy>;

export interface CredentialsProps {
  context: CredentialsContext;
  error?: string;
  onError?: () => void;
  goTo?: {
    accountNumber?: (() => void) | undefined;
    billUpload?: (() => void) | undefined;
  };
  updateStore?: (data: EnrollmentDataSource) => void;
  next: (payload: CredentialsForm) => void;
  overrideCopy?: (context: CredentialsContext) => Partial<CredentialsCopy>;
  skip?: () => void;
  flags?: {
    showFaqButton: boolean;
    allowUtilityReconnection?: boolean;
    allowSkipOnUtilityConnectFailure?: boolean;
    hideUtilityConnectInherentError?: boolean;
    showStep?: boolean;
  };
}

export interface CredentialsForm {
  utilityConnectStatus: UtilityConnectStatus;
}

export interface CredentialsContext {
  verificationStatus?: string;
  utility: Utility;
  utilityConnectToken?: string;
  user: User;
  funnelId?: string;
}
